import { useState } from 'react';
import { Modal, Upload, Button, Alert, Spin, App } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { uploadAttachment } from '@/api/attachments';
import { useExtractTopic } from '@/hooks/useExtractTopic';
import type { ExtractedDraft } from '@/types/extractedTopic';

interface Props {
  open: boolean;
  onClose: () => void;
  onExtracted: (draft: ExtractedDraft) => void;
}

export function ExtractFromImageModal({ open, onClose, onExtracted }: Props) {
  const { message } = App.useApp();
  const [file, setFile] = useState<UploadFile | null>(null);
  const [busy, setBusy] = useState(false);
  const extractMut = useExtractTopic();

  async function run() {
    if (!file?.originFileObj) return;
    setBusy(true);
    try {
      const att = await uploadAttachment(file.originFileObj);
      const res = await extractMut.mutateAsync({ source: 'image', attachment_id: att.id });
      if (res.status === 'failed' || !res.draft) {
        message.error(res.message ?? res.error ?? 'Extraction failed');
      } else {
        onExtracted(res.draft);
        message.success(`Extracted (confidence ${Math.round((res.confidence ?? 0) * 100)}%)`);
        setFile(null);
        onClose();
      }
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} title="Extract from image" onCancel={onClose} footer={null} destroyOnHidden>
      {busy ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin size="large" /></div>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Uploads a flyer image/PDF and fills the English title, content, and structured fields via AI extraction. Review before saving."
          />
          <Upload.Dragger
            accept=".jpg,.jpeg,.png,.pdf"
            maxCount={1}
            beforeUpload={() => false}
            onChange={(info) => setFile(info.fileList[0] ?? null)}
            fileList={file ? [file] : []}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag flyer (image or PDF)</p>
          </Upload.Dragger>
          <Button type="primary" block disabled={!file} onClick={run} style={{ marginTop: 12 }}>Extract</Button>
        </>
      )}
    </Modal>
  );
}
